if (!customElements.get('repillow-delivery-date')) {
  const STORAGE_KEY = 'repillow-delivery-date-v1';
  const NOTE_ASAP = '盡快收貨（3 個工作天內）';
  const NOTE_DATE_SUFFIX = ' 收貨（客人落單時指定）';
  const TZ = 'Asia/Hong_Kong';

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function hkTodayParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());
    const read = (type) => Number(parts.find((part) => part.type === type)?.value);
    return { year: read('year'), month: read('month'), day: read('day') };
  }

  function toYmd(parts) {
    return parts.year + '-' + pad(parts.month) + '-' + pad(parts.day);
  }

  function fromYmd(ymd) {
    const [year, month, day] = String(ymd).split('-').map(Number);
    return { year, month, day };
  }

  function addDays(parts, days) {
    const utc = Date.UTC(parts.year, parts.month - 1, parts.day + days);
    const date = new Date(utc);
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    };
  }

  function weekday(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  }

  function isWorkingDay(parts) {
    const day = weekday(parts);
    return day !== 0 && day !== 6;
  }

  function addWorkingDays(parts, count) {
    let cursor = { ...parts };
    let added = 0;
    while (added < count) {
      cursor = addDays(cursor, 1);
      if (isWorkingDay(cursor)) added += 1;
    }
    return cursor;
  }

  function nextWorkingDay(parts) {
    let cursor = { ...parts };
    while (!isWorkingDay(cursor)) cursor = addDays(cursor, 1);
    return cursor;
  }

  function previousWorkingDay(parts) {
    let cursor = { ...parts };
    while (!isWorkingDay(cursor)) cursor = addDays(cursor, -1);
    return cursor;
  }

  function clampToWindow(ymd, minYmd, maxYmd) {
    let parts = fromYmd(ymd);
    if (!isWorkingDay(parts)) parts = nextWorkingDay(parts);
    let value = toYmd(parts);
    if (value < minYmd) value = minYmd;
    if (value > maxYmd) {
      parts = previousWorkingDay(fromYmd(maxYmd));
      value = toYmd(parts);
      if (value < minYmd) value = minYmd;
    }
    return value;
  }

  function formatDisplay(ymd, lang) {
    const parts = fromYmd(ymd);
    if (String(lang || '').toLowerCase().startsWith('zh')) {
      return parts.year + '年' + parts.month + '月' + parts.day + '日';
    }
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    return new Intl.DateTimeFormat(lang || 'en', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  function readStorage() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || (parsed.mode !== 'asap' && parsed.mode !== 'date')) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeStorage(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Ignore private-mode failures.
    }
  }

  function noteFromState(state) {
    if (state.mode === 'date' && state.date) {
      const parts = fromYmd(state.date);
      return pad(parts.day) + '/' + pad(parts.month) + NOTE_DATE_SUFFIX;
    }
    return '';
  }

  function parseNote(note) {
    const text = String(note || '').trim();
    if (!text) return null;
    if (text === NOTE_ASAP || text.indexOf('盡快') !== -1) return { mode: 'asap' };

    const iso = text.match(/送貨日期：(\d{4}-\d{2}-\d{2})/);
    if (iso) return { mode: 'date', date: iso[1] };

    const dmy = text.match(/(\d{1,2})\/(\d{1,2})\s*收貨/);
    if (dmy) {
      const today = hkTodayParts();
      let year = today.year;
      const day = Number(dmy[1]);
      const month = Number(dmy[2]);
      if (month < today.month || (month === today.month && day < today.day)) year += 1;
      return { mode: 'date', date: year + '-' + pad(month) + '-' + pad(day) };
    }

    return null;
  }

  function cartUpdateUrl() {
    return (window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js';
  }

  function fetchConfig() {
    if (window.theme && theme.utils && theme.utils.fetchConfig) return theme.utils.fetchConfig();
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    };
  }

  function syncNote(state) {
    const body = JSON.stringify({ note: noteFromState(state) });
    return fetch(cartUpdateUrl(), { ...fetchConfig(), body }).catch(() => null);
  }

  if (!window.__repillowDeliveryCheckoutBound) {
    window.__repillowDeliveryCheckoutBound = true;

    const proceedCheckout = (button) => {
      const form = button.form || button.closest('form');
      if (!form) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = '';
      form.appendChild(input);
      HTMLFormElement.prototype.submit.call(form);
    };

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest && event.target.closest('button[name="checkout"]');
        if (!button) return;

        const host = document.querySelector('repillow-delivery-date');
        if (!host || typeof host.commit !== 'function') return;

        event.preventDefault();
        event.stopPropagation();
        host.commit().finally(() => proceedCheckout(button));
      },
      true
    );
  }

  customElements.define(
    'repillow-delivery-date',
    class RepillowDeliveryDate extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        const today = hkTodayParts();
        this.minYmd = toYmd(addWorkingDays(today, 3));
        this.maxYmd = toYmd(addDays(today, 45));

        this.summary = this.querySelector('[data-delivery-summary]');
        this.dateInput = this.querySelector('[data-delivery-date-input]');
        this.asapRadio = this.querySelector('[data-delivery-asap]');
        this.dateRadio = this.querySelector('[data-delivery-specific]');
        this.confirmButton = this.querySelector('[data-delivery-confirm]');

        if (this.dateInput) {
          this.dateInput.min = this.minYmd;
          this.dateInput.max = this.maxYmd;
        }

        const stored = readStorage() || parseNote(this.getAttribute('data-cart-note'));
        this.state = stored && stored.mode === 'date' && stored.date
          ? { mode: 'date', date: clampToWindow(stored.date, this.minYmd, this.maxYmd) }
          : { mode: 'asap' };

        this.render();
        this.bind();
        this.commit();
      }

      bind() {
        this.asapRadio?.addEventListener('change', () => {
          if (this.asapRadio.checked) this.state = { mode: 'asap' };
          this.syncSheet();
        });

        this.dateRadio?.addEventListener('change', () => {
          if (this.dateRadio.checked) {
            const next = clampToWindow(this.dateInput?.value || this.minYmd, this.minYmd, this.maxYmd);
            this.state = { mode: 'date', date: next };
            if (this.dateInput) this.dateInput.value = next;
          }
          this.syncSheet();
        });

        this.dateInput?.addEventListener('change', () => {
          const next = clampToWindow(this.dateInput.value || this.minYmd, this.minYmd, this.maxYmd);
          this.state = { mode: 'date', date: next };
          this.dateInput.value = next;
          if (this.dateRadio) this.dateRadio.checked = true;
          this.syncSheet();
        });

        this.dateInput?.addEventListener('focus', () => {
          if (this.dateRadio) this.dateRadio.checked = true;
          const next = clampToWindow(this.dateInput.value || this.minYmd, this.minYmd, this.maxYmd);
          this.state = { mode: 'date', date: next };
          this.syncSheet();
        });

        this.confirmButton?.addEventListener('click', () => {
          this.commit();
          this.render();
        });
      }

      summaryText() {
        if (this.state.mode === 'date' && this.state.date) {
          return formatDisplay(this.state.date, document.documentElement.lang);
        }
        return this.getAttribute('data-asap-short') || '';
      }

      render() {
        if (this.summary) this.summary.textContent = this.summaryText();
        this.syncSheet();
      }

      syncSheet() {
        if (this.asapRadio) this.asapRadio.checked = this.state.mode === 'asap';
        if (this.dateRadio) this.dateRadio.checked = this.state.mode === 'date';
        if (this.dateInput) {
          this.dateInput.value = this.state.date || this.minYmd;
          this.dateInput.disabled = this.state.mode !== 'date';
        }
      }

      commit() {
        writeStorage(this.state);
        this.render();
        return syncNote(this.state);
      }
    }
  );
}
